/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 22nd 2022, 7:57:29 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useDidMount } from "./useDidMount";

interface UseDebounceFnOptions {
  timeout: number;
  deps: any[];
  doDebounce?: boolean;
}
/** Hook to debounce a function. Dependency list must be primitive values */
export const useDebounceFn = (func: () => void, options: UseDebounceFnOptions) => {
  const { timeout, deps, doDebounce = true } = options;

  // Excellent idea to use setTimeout to debounce a function. Idea from https://stackoverflow.com/questions/42217121/how-to-start-search-only-when-user-stops-typing
  const debounceFunction = () => {
    const debounceTimeout: NodeJS.Timeout = setTimeout(() => {
      func();
    }, timeout);

    // Cleaning up before unmounting for next render
    return () => {
      clearTimeout(debounceTimeout);
    };
  };

  const didMount = useDidMount();

  React.useEffect(() => {
    if (!didMount) return; // Do not run the function on the first mount. Note: didMount is a ref so do not include it in dependency array

    if (doDebounce) {
      return debounceFunction(); // Making sure to return the useEffect clean up function from the debounce
    } else {
      func();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, doDebounce]);
};
